import { FC, ReactNode, useState } from 'react';
import { useSelector } from 'react-redux';
import { Dropdown, Menu, Popconfirm, Tooltip } from 'antd';
import styles from './styles.module.less';

export interface IconToggleOption {
  label: string;
  icon: ReactNode;
  tooltip?: string;
  confirmTitle: string;
}

export interface IconToggleDropdownProps {
  value?: boolean;
  onChange?: (value: boolean) => void;
  trueOption: IconToggleOption;
  falseOption: IconToggleOption;
  menuTitle?: string;
  disabled?: boolean;
  placement?: 'bottomLeft' | 'bottomRight' | 'topLeft' | 'topRight';
  label?: string;
  hideIcon?: boolean;
  block?: boolean;
}

// A missing value is treated as true, so toggles default to their "on" option.
const IconToggleDropdown: FC<IconToggleDropdownProps> = ({
  value,
  onChange,
  trueOption,
  falseOption,
  menuTitle,
  disabled = false,
  placement = 'bottomRight',
  label = '',
  hideIcon = false,
  block = false,
}) => {
  const isSuper = useSelector((store: any) => store.auth.user?.is_super);
  const isReadOnly = disabled || !isSuper;
  const currentValue = value !== false;
  const currentOption = currentValue ? trueOption : falseOption;
  const [pendingValue, setPendingValue] = useState<boolean | null>(null);
  const [menuOpen, setMenuOpen] = useState(false);

  const handleSelect = (nextValue: boolean) => {
    if (nextValue === currentValue) {
      return;
    }

    setPendingValue(nextValue);
  };

  const confirmChange = () => {
    if (pendingValue !== null) {
      onChange?.(pendingValue);
    }
    setPendingValue(null);
  };

  const menuItems = [
    <Menu.Item key="true" onClick={() => handleSelect(true)}>
      {trueOption.label}
    </Menu.Item>,
    <Menu.Item key="false" onClick={() => handleSelect(false)}>
      {falseOption.label}
    </Menu.Item>,
  ];

  const menu = (
    <Menu selectedKeys={[String(currentValue)]}>
      {menuTitle ? <Menu.ItemGroup title={menuTitle}>{menuItems}</Menu.ItemGroup> : menuItems}
    </Menu>
  );

  const containerStyle = {
    display: block ? 'flex' : 'inline-flex',
    alignItems: 'center',
    width: block ? '100%' : undefined,
  };

  const icon = !hideIcon && (
    <Tooltip title={pendingValue === null && !menuOpen ? currentOption.tooltip : undefined}>
      <span style={{ display: 'inline-flex', alignItems: 'center', flexShrink: 0 }}>{currentOption.icon}</span>
    </Tooltip>
  );

  if (isReadOnly) {
    return (
      <span style={containerStyle}>
        {icon}
        {label && <span className={`${styles.label} ${styles.disabledLabel}`}>{label}</span>}
      </span>
    );
  }

  return (
    <Popconfirm
      visible={pendingValue !== null}
      onConfirm={confirmChange}
      onCancel={() => setPendingValue(null)}
      placement="bottom"
      title={pendingValue === false ? falseOption.confirmTitle : trueOption.confirmTitle}
    >
      <Dropdown overlay={menu} placement={placement} trigger={['click']} onVisibleChange={setMenuOpen}>
        <span style={{ ...containerStyle, cursor: 'pointer' }}>
          {icon}
          {label && <span className={styles.label}>{label}</span>}
        </span>
      </Dropdown>
    </Popconfirm>
  );
};

export default IconToggleDropdown;
